import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Dokument } from 'src/app/domain/model/dokument';
import { Reply } from 'src/app/domain/model/reply';
import { Zuweisung } from 'src/app/domain/model/zuweisung';
import { environment } from '../../../environments/environment';
import { Aufgabe } from '../../domain/model/aufgabe';
import { tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AufgabeService {
    private http = inject(HttpClient);

    public listAufgabeByOrganisation(orgId: string): Observable<Aufgabe[]> {
        return this.http.get(environment.API_URL + '/organisation/' + orgId + '/aufgaben') as Observable<Aufgabe[]>;
    }

    public listZuweisungByOrganisation(orgId: string): Observable<Zuweisung[]> {
        return this.http.get(environment.API_URL + '/organisation/' + orgId + '/zuweisungen') as Observable<Zuweisung[]>;
    }

    public async createAufgabe(aufgabeJson: string): Promise<Aufgabe> {
        return this.http.post<Aufgabe>(environment.API_URL + '/aufgabe', aufgabeJson)
            .toPromise()
            .then((resp) => resp)
            .catch((error) => {
                throw error;
            });
    }

    public async sendMailsForAufgaben(id: number): Promise<boolean> {
        return this.http.put(environment.API_URL + '/aufgabe/' + id + '/sendMails', '')
            .toPromise()
            .then(() => true)
            .catch(() => null);
    }

    public async attachDocument(aufgabe: Aufgabe, file: File, callback: any): Promise<boolean> {
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        const req = new HttpRequest('POST',
            environment.API_URL + '/aufgabe/' + aufgabe.id + '/dokument',
            formData, {
                reportProgress: true,
            });
        return this.http.request(req).pipe(
            tap(event => callback.handleUploadEvent(event)),
        ).toPromise()
            .then(() => true)
            .catch((reason) => {
                console.log('reason:' + reason);
                return false;
            });
    }

    public getAufgabe(id: string): Observable<Aufgabe> {
        return this.http.get(environment.API_URL + '/aufgabe/' + id) as Observable<Aufgabe>;
    }

    public deleteAufgabe(id: string, sendMails: boolean = true): Promise<boolean> {
        return this.http.delete(environment.API_URL + '/aufgabe/' + id + '?sendMails=' + sendMails)
            .toPromise()
            .then(() => true)
            .catch(() => false);
    }

    public listCreatorDocumentsAsCreator(id: string): Observable<Dokument[]> {
        return this.http.get(environment.API_URL + '/aufgabe/' + id + '/dokumente?target=creator') as Observable<Dokument[]>;
    }

    public listCreaterDocumentAsReceiver(id: string): Observable<Dokument[]> {
        return this.http.get(environment.API_URL + '/aufgabe/' + id + '/dokumente?target=receiver') as Observable<Dokument[]>;
    }

    public listAllReplies(id: string): Observable<Reply[]> {
        return this.http.get(environment.API_URL + '/aufgabe/' + id + '/replies') as Observable<Reply[]>;
    }

    public getZuweisung(id: string): Observable<Zuweisung> {
        return this.http.get(environment.API_URL + '/zuweisung/' + id) as Observable<Zuweisung>;
    }

    public async markZuweisungDone(id: string): Promise<boolean> {
        return this.http.put(environment.API_URL + '/zuweisung/' + id, '')
            .toPromise()
            .then(() => true)
            .catch(() => false);
    }

    public async storeReply(zuweisung: Zuweisung, file: File, callback: any): Promise<boolean> {
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        const req = new HttpRequest('POST',
            environment.API_URL + '/zuweisung/' + zuweisung.id + '/dokument',
            formData, {
                reportProgress: true,
            });
        return this.http.request(req).pipe(
            tap(event => callback.handleUploadEvent(event)),
        ).toPromise()
            .then(() => true)
            .catch((error) => {
                console.log(error);
                throw error;
            });
    }

    public listReplies(id: string): Observable<Dokument[]> {
        return this.http.get(environment.API_URL + '/zuweisung/' + id + '/dokumente') as Observable<Dokument[]>;
    }

    public removeReply(id: string, fileName: string): Promise<boolean> {
        return this.http.delete(environment.API_URL + '/zuweisung/' + id + '/dokument/' + fileName)
            .toPromise()
            .then(() => true)
            .catch(() => false);
    }

    alleZuweisungenGelesen(aufgabe: Aufgabe) {
        for (const zuweisung of aufgabe.zuweisungen) {
            if (!zuweisung.gelesen) {
                return false;
            }
        }
        return true;
    }
}
